import { Grid } from "@mui/material";
import { Typography } from "@financeable-com-au/financeable-ui";
import { ASSET_DETAILS_PANEL, ASSET_SPECIFICATION_FIELDS, ASSET_SPECIFICATION_VALUATION } from "@/constants";
import { AssetSpecificationDetailsProps } from "@/utils/interface";
import { Asset, AutograbSpecifications, LoanDetails } from "@/store/slices/types/applicationFormSlice";
import { cleanNumber, formatCurrencyValue, formatRangeLowToHigh, toFiniteNumber } from "@/utils/formatNumber";
import { hasValue } from "@/utils/commonFunctions";
import AssetDetailsLayout, { InfoRow } from "./AssetDetailsLayout";

/** The payload names the same thing more than one way, so the first key that carries a value wins. */
const firstValue = (specifications: AutograbSpecifications, keys: string[]) =>
  keys.map((key) => specifications?.[key]).find((value) => hasValue(value));

/** Units arrive inconsistently — "1984" from one key, "1984 cc" from its alias — so never double them up. */
const withUnit = (value: string | number, suffix?: string) => {
  const formatted = cleanNumber(value);
  if (!suffix || formatted.toLowerCase().includes(suffix.trim().toLowerCase())) return formatted;
  return `${formatted}${suffix}`;
};

const SpecificationsSection = ({
  specifications,
  asset,
  loanDetails,
}: {
  specifications: AutograbSpecifications;
  asset: Asset;
  loanDetails: LoanDetails;
}) => {
  // What the deal actually finances, which is the numerator of the LVR the lender will see.
  const amountFinanced =
    toFiniteNumber(asset?.assetValue) -
    toFiniteNumber(loanDetails?.deposit) -
    toFiniteNumber(loanDetails?.tradeIn) +
    toFiniteNumber(loanDetails?.payout);

  // Valued against retail, falling back through the figures AutoGrab returns for cheaper matches.
  const retail = toFiniteNumber(firstValue(specifications, ["RetailPrice", "Price", "RRP"]));
  const estimatedLVR = retail > 0 ? ((amountFinanced / retail) * 100).toFixed(2) : null;

  const valuationRows: { label: string; value: string }[] = [
    ...(estimatedLVR ? [{ label: ASSET_SPECIFICATION_VALUATION.ESTIMATED_LVR, value: `${estimatedLVR}%` }] : []),
    // Each figure is shown with the range AutoGrab struck it within, so the point estimate reads in context.
    {
      label: ASSET_SPECIFICATION_VALUATION.RETAIL,
      value: formatCurrencyValue(firstValue(specifications, ["RetailPrice", "Price"])),
    },
    {
      label: ASSET_SPECIFICATION_VALUATION.RETAIL_RANGE,
      value: formatRangeLowToHigh(specifications?.RetailLower, specifications?.RetailUpper),
    },
    {
      label: ASSET_SPECIFICATION_VALUATION.TRADE,
      value: formatCurrencyValue(specifications?.TradePrice),
    },
    {
      label: ASSET_SPECIFICATION_VALUATION.TRADE_RANGE,
      value: formatRangeLowToHigh(specifications?.TradeLower, specifications?.TradeUpper),
    },
    { label: ASSET_SPECIFICATION_VALUATION.RRP, value: formatCurrencyValue(specifications?.RRP) },
    ...(hasValue(specifications?.KMS)
      ? [{ label: ASSET_SPECIFICATION_VALUATION.VALUATION_KMS, value: `${cleanNumber(specifications?.KMS)} km` }]
      : []),
  ];

  const specificationRows = ASSET_SPECIFICATION_FIELDS.map(({ keys, label, suffix }) => ({
    label,
    value: firstValue(specifications, keys),
    suffix,
  })).filter(({ value }) => hasValue(value));

  return (
    <Grid container item xs={12}>
      <Typography level="body-sm" fontWeight={600} style={{ margin: "0 0 6px" }}>
        {ASSET_DETAILS_PANEL.VALUATION_HEADING}
      </Typography>
      {valuationRows.map(({ label, value }) => (
        <InfoRow key={label} label={`${label}:`} value={value} />
      ))}

      {specificationRows.length > 0 && (
        <Typography level="body-sm" fontWeight={600} style={{ margin: "10px 0 6px" }}>
          {ASSET_DETAILS_PANEL.VEHICLE_HEADING}
        </Typography>
      )}
      {specificationRows.map(({ label, value, suffix }) => (
        <InfoRow key={label} label={`${label}:`} value={withUnit(value as string | number, suffix)} />
      ))}
    </Grid>
  );
};

/**
 * The asset-details panel for a vehicle identified through AutoGrab, which stamps its own vehicle id
 * on the asset. It reads the same as the Glass's panel — valuation first, then the vehicle itself —
 * but AutoGrab prices with a range around each figure and describes the vehicle in more detail, so
 * the rows are its own.
 */
const AssetSpecificationDetails = ({ asset, loanDetails }: AssetSpecificationDetailsProps) => {
  const specifications = asset?.specifications as AutograbSpecifications | undefined;
  // The panel is only reached with a vehicle id on the asset; the specification can still be missing
  // if the vehicle was identified before the payload was persisted.
  const isSpecificationData = Boolean(specifications && hasValue(specifications?.YearCreate));

  return (
    <AssetDetailsLayout
      asset={asset}
      heading={ASSET_DETAILS_PANEL.DEFAULT_SPECIFICATION_HEADING}
      specifications={
        isSpecificationData ? (
          <SpecificationsSection
            specifications={specifications as AutograbSpecifications}
            asset={asset}
            loanDetails={loanDetails}
          />
        ) : null
      }
    />
  );
};

export default AssetSpecificationDetails;
